import { components } from "@/api-schema";
import { MergeFieldCategory, MergeSelection } from "@/api-schema-enums";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { AlertTriangle, GitMerge } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MergeConflictList } from "../merge-conflict-list/merge-conflict-list";
import { MergeFieldRow } from "../merge-field-row";

type MergePreviewResponse = components["schemas"]["MergePreviewResponse"];
type MergeRelationPreview = components["schemas"]["MergeRelationPreviewResponse"];
type MergeSelections = components["schemas"]["MergeExecuteBody"]["selections"];

const LOGO_FIELD_KEY = "logoPath";

interface ChoosePropertiesStepProps {
  preview: MergePreviewResponse;
  choices: MergeSelections;
  sourceLogoUrl?: string;
  targetLogoUrl?: string;
  onChoiceChange: (key: string, value: MergeSelection) => void;
  onRecheckConflicts: () => void;
  isRecheckingConflicts: boolean;
}

export const ChoosePropertiesStep = ({
  preview,
  choices,
  sourceLogoUrl,
  targetLogoUrl,
  onChoiceChange,
  onRecheckConflicts,
  isRecheckingConflicts,
}: ChoosePropertiesStepProps) => {
  const { t } = useTranslation("masterdata");
  const { source, target, fields, relations, conflicts } = preview;

  const identifierFields = fields.filter((field) => field.category === MergeFieldCategory.Identifier);
  const attributeFields = fields.filter((field) => field.category !== MergeFieldCategory.Identifier);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
        <div className="bg-destructive/10 border-destructive/20 min-w-0 rounded-md border px-3 py-1.5 text-center">
          <p className="text-destructive text-xs font-semibold">{t("WillBeDeleted")}</p>
          <p className="text-sm font-medium wrap-break-word">{source.label}</p>
        </div>
        <div className="w-20 self-center text-center">
          <GitMerge className="text-muted-foreground mx-auto h-4 w-4" />
        </div>
        <div className="bg-primary/10 border-primary/20 min-w-0 rounded-md border px-3 py-1.5 text-center">
          <p className="text-primary text-xs font-semibold">{t("WillBeKept")}</p>
          <p className="text-sm font-medium wrap-break-word">{target.label}</p>
        </div>
      </div>

      {attributeFields.length > 0 && (
        <div>
          {attributeFields.map((field) => {
            const isLogoField = field.key === LOGO_FIELD_KEY;

            return (
              <MergeFieldRow
                key={field.key}
                field={field}
                choice={choices[field.key] ?? MergeSelection.Target}
                onChange={(value) => onChoiceChange(field.key, value)}
                sourceValueNode={
                  isLogoField ? (
                    <BrandLogo
                      brand={{ name: source.label, logoUrl: sourceLogoUrl }}
                      variant={BrandLogoVariant.MEDIUM}
                    />
                  ) : undefined
                }
                targetValueNode={
                  isLogoField ? (
                    <BrandLogo
                      brand={{ name: target.label, logoUrl: targetLogoUrl }}
                      variant={BrandLogoVariant.MEDIUM}
                    />
                  ) : undefined
                }
              />
            );
          })}
        </div>
      )}

      {identifierFields.length > 0 && (
        <div className="border-warning/40 bg-warning/5 mt-8 space-y-2 rounded-md border px-3 py-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-warning h-4 w-4 shrink-0" />
            <p className="text-warning flex-1 text-sm font-semibold">{t("MergeIdentifiersTitle")}</p>
          </div>
          <p className="text-muted-foreground text-xs">{t("MergeIdentifiersWarning")}</p>
          <div>
            {identifierFields.map((field) => (
              <MergeFieldRow
                key={field.key}
                field={field}
                choice={choices[field.key] ?? MergeSelection.Target}
                onChange={(value) => onChoiceChange(field.key, value)}
              />
            ))}
          </div>
        </div>
      )}

      {relations.length > 0 && (
        <div className="border-border bg-muted/20 mt-8 space-y-1 rounded-md border px-3 py-2">
          <p className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
            {t("RelationsMergedAutomatically")}
          </p>
          {relations.map((relation: MergeRelationPreview) => (
            <div key={relation.key} className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground min-w-0 flex-1 truncate">
                {t(`MergeRelation-${relation.key}`, { defaultValue: relation.key })}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span>{relation.sourceCount}</span>
                <span className="text-muted-foreground">+</span>
                <span>{relation.targetCount}</span>
                <span className="text-muted-foreground">=</span>
                <span className="font-medium">{relation.resultCount}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      <MergeConflictList conflicts={conflicts} onRecheck={onRecheckConflicts} isRechecking={isRecheckingConflicts} />
    </div>
  );
};
